import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from 'react-select';
import Layout from '../components/Layout/Layout';

const API_BASE = process.env.REACT_APP_API_URL || '';

function getAuthHeaders() {
  const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function apiFetch(url, options = {}) {
  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers: { ...getAuthHeaders(), ...(options.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

// --- Styles ---
const cardStyle = { background: '#fff', borderRadius: '8px', padding: '20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const btnPrimary = { padding: '8px 16px', borderRadius: '6px', border: 'none', background: '#667eea', color: '#fff', cursor: 'pointer', fontWeight: '600', fontSize: '13px' };
const btnDanger = { ...btnPrimary, background: '#e74c3c' };
const btnSecondary = { ...btnPrimary, background: '#f0f0f0', color: '#333' };
const btnSmall = { padding: '4px 10px', fontSize: '12px', borderRadius: '5px', border: 'none', cursor: 'pointer', fontWeight: '600' };
const inputStyle = { width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '4px' };
const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalBox = { background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '520px', width: '90%', maxHeight: '85vh', overflow: 'auto' };
const thStyle = { padding: '10px 12px', fontSize: '13px', color: '#666', textAlign: 'left' };
const tdStyle = { padding: '10px 12px', fontSize: '14px' };

const StampbookConfiguration = () => {
  const [slots, setSlots] = useState([]);
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Edit modal
  const [editSlot, setEditSlot] = useState(null);
  const [editForm, setEditForm] = useState({ attraction_id: '', display_name: '', icon: '', description: '', radius_m: '', notes: '' });
  const [editWipeExisting, setEditWipeExisting] = useState(false);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ attraction_id: '', display_name: '', icon: '', description: '', radius_m: '', notes: '' });

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null); // slot object
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Grant/Revoke modal (from slot row actions)
  const [grantModal, setGrantModal] = useState(null); // { stamp_id, action: 'grant'|'revoke' }
  const [grantForm, setGrantForm] = useState({ user_id: '', reason: '' });

  // Manual Stamp Management section
  const [lookupUserId, setLookupUserId] = useState('');
  const [userStamps, setUserStamps] = useState(null);
  const [userStampsLoading, setUserStampsLoading] = useState(false);
  const [manualGrantModal, setManualGrantModal] = useState(null); // { stamp_id, action, user_id }
  const [manualGrantReason, setManualGrantReason] = useState('');

  // Audit log
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(0);
  const AUDIT_LIMIT = 50;

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/api/stampbook/slots');
      setSlots(data.slots || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAttractions = useCallback(async () => {
    try {
      const data = await apiFetch('/api/attractions?limit=1000');
      const list = Array.isArray(data) ? data : Array.isArray(data.data) ? data.data : [];
      setAttractions(list);
    } catch (err) {
      console.error('Failed to fetch attractions:', err);
      setAttractions([]);
    }
  }, []);

  useEffect(() => {
    fetchSlots();
    fetchAttractions();
  }, [fetchSlots, fetchAttractions]);

  const flash = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const getAttractionName = (id) => {
    if (!id) return 'Unassigned';
    const a = attractions.find(a => a.id === id);
    return a ? a.name : `ID ${id}`;
  };

  // --- Create slot ---
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiFetch('/api/stampbook/slots', {
        method: 'POST',
        body: JSON.stringify({
          attraction_id: createForm.attraction_id ? Number(createForm.attraction_id) : null,
          display_name: createForm.display_name || null,
          icon: createForm.icon || null,
          description: createForm.description || null,
          radius_m: createForm.radius_m ? Number(createForm.radius_m) : null,
          notes: createForm.notes || null,
        }),
      });
      setShowCreate(false);
      setCreateForm({ attraction_id: '', display_name: '', icon: '', description: '', radius_m: '', notes: '' });
      flash('Slot created successfully');
      fetchSlots();
    } catch (err) {
      setError(err.message);
    }
  };

  // --- Update slot ---
  const openEdit = (slot) => {
    setEditSlot(slot);
    setEditForm({
      attraction_id: slot.attraction_id || '',
      display_name: slot.display_name || '',
      icon: slot.icon || '',
      description: slot.description || '',
      radius_m: slot.radius_m || '',
      notes: slot.notes || '',
    });
    setEditWipeExisting(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const attractionChanged = editForm.attraction_id !== (editSlot.attraction_id || '').toString()
        && (editForm.attraction_id || editSlot.attraction_id);
      const wipeParam = attractionChanged && editWipeExisting ? '?wipe_existing=true' : '';

      await apiFetch(`/api/stampbook/slots/${editSlot.stamp_id}${wipeParam}`, {
        method: 'PUT',
        body: JSON.stringify({
          attraction_id: editForm.attraction_id ? Number(editForm.attraction_id) : null,
          display_name: editForm.display_name || null,
          icon: editForm.icon || null,
          description: editForm.description || null,
          radius_m: editForm.radius_m ? Number(editForm.radius_m) : null,
          notes: editForm.notes || null,
        }),
      });
      setEditSlot(null);
      flash('Slot updated successfully');
      fetchSlots();
    } catch (err) {
      setError(err.message);
    }
  };

  // Attraction changed in edit?
  const attractionChangedInEdit = editSlot && editForm.attraction_id !== (editSlot.attraction_id || '').toString()
    && (editForm.attraction_id || editSlot.attraction_id);

  // --- Deactivate (soft delete) ---
  const handleDeactivate = async (slot) => {
    try {
      await apiFetch(`/api/stampbook/slots/${slot.stamp_id}`, { method: 'DELETE' });
      flash(slot.is_active ? 'Slot deactivated' : 'Slot deleted');
      fetchSlots();
    } catch (err) {
      setError(err.message);
    }
  };

  // --- Reactivate ---
  const handleReactivate = async (slot) => {
    try {
      await apiFetch(`/api/stampbook/slots/${slot.stamp_id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: true }),
      });
      flash('Slot reactivated');
      fetchSlots();
    } catch (err) {
      setError(err.message);
    }
  };

  // --- Hard delete ---
  const handleHardDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`/api/stampbook/slots/${deleteTarget.stamp_id}?hard=true`, { method: 'DELETE' });
      setDeleteTarget(null);
      setDeleteConfirmText('');
      flash('Slot permanently deleted');
      fetchSlots();
    } catch (err) {
      setError(err.message);
    }
  };

  // --- Grant / Revoke (from slot row) ---
  const handleGrantRevoke = async (e) => {
    e.preventDefault();
    if (!grantForm.reason.trim() || grantForm.reason.trim().length < 5) {
      setError('Reason is required (min 5 characters)');
      return;
    }
    try {
      const endpoint = grantModal.action === 'grant' ? '/api/stampbook/admin/grant' : '/api/stampbook/admin/revoke';
      await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          user_id: Number(grantForm.user_id),
          stamp_id: grantModal.stamp_id,
          reason: grantForm.reason.trim(),
        }),
      });
      setGrantModal(null);
      setGrantForm({ user_id: '', reason: '' });
      flash(`Stamp ${grantModal.action === 'grant' ? 'granted' : 'revoked'} successfully`);
    } catch (err) {
      setError(err.message);
    }
  };

  // --- Manual Stamp Management: lookup user ---
  const handleUserLookup = async (e) => {
    e.preventDefault();
    if (!lookupUserId) return;
    setUserStampsLoading(true);
    setUserStamps(null);
    try {
      const data = await apiFetch(`/api/stampbook/user/${lookupUserId}`);
      setUserStamps(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setUserStampsLoading(false);
    }
  };

  // --- Manual grant/revoke from user lookup ---
  const handleManualGrantRevoke = async (e) => {
    e.preventDefault();
    if (!manualGrantReason.trim() || manualGrantReason.trim().length < 5) {
      setError('Reason is required (min 5 characters)');
      return;
    }
    try {
      const endpoint = manualGrantModal.action === 'grant' ? '/api/stampbook/admin/grant' : '/api/stampbook/admin/revoke';
      await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          user_id: Number(manualGrantModal.user_id),
          stamp_id: manualGrantModal.stamp_id,
          reason: manualGrantReason.trim(),
        }),
      });
      setManualGrantModal(null);
      setManualGrantReason('');
      flash(`Stamp ${manualGrantModal.action === 'grant' ? 'granted' : 'revoked'} successfully`);
      // Refresh user stamps
      const data = await apiFetch(`/api/stampbook/user/${lookupUserId}`);
      setUserStamps(data);
    } catch (err) {
      setError(err.message);
    }
  };

  // --- Audit log ---
  const fetchAuditLog = async (page = 0) => {
    setAuditLoading(true);
    try {
      const data = await apiFetch(`/api/stampbook/admin/audit-log?limit=${AUDIT_LIMIT}&offset=${page * AUDIT_LIMIT}`);
      setAuditLog(data.actions || []);
      setAuditTotal(data.total || 0);
      setAuditPage(page);
      setShowAudit(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setAuditLoading(false);
    }
  };

  const toggleAudit = () => {
    if (showAudit) {
      setShowAudit(false);
    } else {
      fetchAuditLog(0);
    }
  };

  // Separate active/inactive slots
  const activeSlots = slots.filter(s => s.is_active);
  const inactiveSlots = slots.filter(s => !s.is_active);

  // --- react-select options derived from attractions ---
  const attractionOptions = useMemo(() => {
    if (!Array.isArray(attractions)) return [];
    return attractions.map(a => ({ value: String(a.id), label: a.name }));
  }, [attractions]);

  const reactSelectStyles = {
    control: (base) => ({ ...base, borderColor: '#ddd', fontSize: '14px', minHeight: '38px' }),
    menu: (base) => ({ ...base, zIndex: 1050 }),
  };

  // --- Slot form fields (shared between create/edit) ---
  const renderSlotForm = (form, setForm) => (
    <>
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>Attraction</label>
        <Select
          styles={reactSelectStyles}
          options={attractionOptions}
          value={attractionOptions.find(o => o.value === String(form.attraction_id)) || null}
          onChange={(selected) => setForm(p => ({ ...p, attraction_id: selected ? selected.value : '' }))}
          isClearable
          isSearchable
          isDisabled={attractionOptions.length === 0}
          placeholder={attractionOptions.length === 0 ? 'No attractions available' : 'Search and select an attraction...'}
          noOptionsMessage={() => 'No matching attractions'}
        />
      </div>
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>Display Name</label>
        <input style={inputStyle} value={form.display_name} onChange={e => setForm(p => ({ ...p, display_name: e.target.value }))} placeholder="e.g. A Famosa Fort" maxLength={100} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={labelStyle}>Icon (emoji)</label>
          <input style={inputStyle} value={form.icon} onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} placeholder="e.g. 🏰" maxLength={50} />
        </div>
        <div>
          <label style={labelStyle}>Radius (meters)</label>
          <input style={inputStyle} type="number" min="10" max="5000" value={form.radius_m} onChange={e => setForm(p => ({ ...p, radius_m: e.target.value }))} placeholder="Default: 200" />
        </div>
      </div>
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>Description</label>
        <input style={inputStyle} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Short description for users" />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Admin Notes (internal)</label>
        <textarea style={{ ...inputStyle, minHeight: '60px' }} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes..." maxLength={500} />
      </div>
    </>
  );

  return (
    <Layout title="Stampbook Configuration">
      <div style={{ padding: '20px' }}>
        {successMsg && (
          <div style={{ padding: '12px', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '8px', marginBottom: '16px', color: '#155724' }}>
            {successMsg}
          </div>
        )}
        {error && (
          <div style={{ padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '8px', marginBottom: '16px', color: '#c33' }}>
            {error}
            <button onClick={() => setError(null)} style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer', color: '#c33', fontWeight: 'bold' }}>X</button>
          </div>
        )}

        {/* Actions bar */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button style={btnPrimary} onClick={() => setShowCreate(true)}>+ New Slot</button>
          <button style={btnSecondary} onClick={toggleAudit}>
            {showAudit ? 'Hide Audit Log' : 'View Audit Log'}
          </button>
        </div>

        {/* ========== SLOT LIST ========== */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Loading slots...</div>
        ) : (
          <>
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
                Active Slots ({activeSlots.length})
              </h3>
              {activeSlots.length === 0 ? (
                <p style={{ color: '#888' }}>No active slots. Click "+ New Slot" to create one.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #eee' }}>
                        <th style={thStyle}>#</th>
                        <th style={thStyle}>Icon</th>
                        <th style={thStyle}>Display Name</th>
                        <th style={thStyle}>Attraction</th>
                        <th style={thStyle}>Radius</th>
                        <th style={thStyle}>Unlocks</th>
                        <th style={thStyle}>Notes</th>
                        <th style={thStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSlots.map((slot) => (
                        <tr key={slot.stamp_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={tdStyle}>{slot.stamp_id}</td>
                          <td style={{ ...tdStyle, fontSize: '20px' }}>{slot.icon || '📍'}</td>
                          <td style={tdStyle}>{slot.display_name || '-'}</td>
                          <td style={{ ...tdStyle, color: slot.attraction_id ? '#333' : '#aaa' }}>
                            {getAttractionName(slot.attraction_id)}
                          </td>
                          <td style={tdStyle}>{slot.radius_m ? `${slot.radius_m}m` : 'Default'}</td>
                          <td style={tdStyle}>
                            <span style={{ background: '#e8f0fe', color: '#1a73e8', padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '600' }}>
                              {slot.unlock_count || 0}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, fontSize: '13px', color: '#888', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {slot.notes || '-'}
                          </td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              <button style={{ ...btnSmall, background: '#667eea', color: '#fff' }} onClick={() => openEdit(slot)}>Edit</button>
                              <button style={{ ...btnSmall, background: '#f39c12', color: '#fff' }} onClick={() => handleDeactivate(slot)}>Deactivate</button>
                              <button style={{ ...btnSmall, background: '#e74c3c', color: '#fff' }} onClick={() => { setDeleteTarget(slot); setDeleteConfirmText(''); }}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Inactive slots */}
            {inactiveSlots.length > 0 && (
              <div style={cardStyle}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#888' }}>
                  Inactive Slots ({inactiveSlots.length})
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #eee' }}>
                        <th style={thStyle}>#</th>
                        <th style={thStyle}>Display Name</th>
                        <th style={thStyle}>Attraction</th>
                        <th style={thStyle}>Unlocks</th>
                        <th style={thStyle}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inactiveSlots.map((slot) => (
                        <tr key={slot.stamp_id} style={{ borderBottom: '1px solid #f0f0f0', opacity: 0.7 }}>
                          <td style={tdStyle}>{slot.stamp_id}</td>
                          <td style={tdStyle}>{slot.icon || '📍'} {slot.display_name || '-'}</td>
                          <td style={{ ...tdStyle, color: '#aaa' }}>{getAttractionName(slot.attraction_id)}</td>
                          <td style={tdStyle}>{slot.unlock_count || 0}</td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button style={{ ...btnSmall, background: '#43e97b', color: '#000' }} onClick={() => handleReactivate(slot)}>Reactivate</button>
                              <button style={{ ...btnSmall, background: '#e74c3c', color: '#fff' }} onClick={() => { setDeleteTarget(slot); setDeleteConfirmText(''); }}>Hard Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* ========== MANUAL STAMP MANAGEMENT ========== */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Manual Stamp Management</h3>
          <form onSubmit={handleUserLookup} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '16px' }}>
            <div style={{ flex: '0 0 200px' }}>
              <label style={labelStyle}>User ID</label>
              <input style={inputStyle} type="number" value={lookupUserId} onChange={e => setLookupUserId(e.target.value)} placeholder="Enter user ID" required />
            </div>
            <button type="submit" style={btnPrimary} disabled={userStampsLoading}>
              {userStampsLoading ? 'Loading...' : 'Look Up'}
            </button>
          </form>

          {userStamps && (
            <div>
              <p style={{ fontSize: '14px', marginBottom: '12px', color: '#555' }}>
                User #{lookupUserId}: <strong>{userStamps.total_unlocked}/{userStamps.total_active_slots}</strong> stamps collected
                {userStamps.completed && <span style={{ marginLeft: '8px', padding: '2px 8px', background: '#d4edda', borderRadius: '4px', fontSize: '12px', fontWeight: '600', color: '#155724' }}>COMPLETED</span>}
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #eee' }}>
                    <th style={thStyle}>Slot</th>
                    <th style={thStyle}>Attraction</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Unlocked At</th>
                    <th style={thStyle}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {userStamps.stamps.map((stamp) => (
                    <tr key={stamp.stamp_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={tdStyle}>{stamp.icon || '📍'} {stamp.display_name || `Slot ${stamp.stamp_id}`}</td>
                      <td style={{ ...tdStyle, color: stamp.attraction_name ? '#333' : '#aaa' }}>{stamp.attraction_name || 'Unassigned'}</td>
                      <td style={tdStyle}>
                        {stamp.unlocked ? (
                          <span style={{ padding: '2px 8px', background: '#d4edda', borderRadius: '4px', fontSize: '11px', fontWeight: '600', color: '#155724' }}>Granted</span>
                        ) : (
                          <span style={{ padding: '2px 8px', background: '#f8f9fa', borderRadius: '4px', fontSize: '11px', fontWeight: '600', color: '#888' }}>Not Granted</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, fontSize: '13px', color: '#888' }}>
                        {stamp.unlocked_at ? new Date(stamp.unlocked_at).toLocaleString() : '-'}
                      </td>
                      <td style={tdStyle}>
                        {stamp.unlocked ? (
                          <button
                            style={{ ...btnSmall, background: '#fa709a', color: '#fff' }}
                            onClick={() => { setManualGrantModal({ stamp_id: stamp.stamp_id, action: 'revoke', user_id: lookupUserId }); setManualGrantReason(''); }}
                          >Revoke</button>
                        ) : (
                          <button
                            style={{ ...btnSmall, background: '#43e97b', color: '#000' }}
                            onClick={() => { setManualGrantModal({ stamp_id: stamp.stamp_id, action: 'grant', user_id: lookupUserId }); setManualGrantReason(''); }}
                          >Grant</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ========== AUDIT LOG ========== */}
        {showAudit && (
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>
              Audit Log ({auditTotal} entries)
            </h3>
            {auditLoading ? (
              <p style={{ color: '#888' }}>Loading...</p>
            ) : auditLog.length === 0 ? (
              <p style={{ color: '#888' }}>No audit entries yet.</p>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '700px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                        <th style={{ padding: '8px' }}>Time</th>
                        <th style={{ padding: '8px' }}>Admin</th>
                        <th style={{ padding: '8px' }}>User</th>
                        <th style={{ padding: '8px' }}>Slot</th>
                        <th style={{ padding: '8px' }}>Action</th>
                        <th style={{ padding: '8px' }}>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.map((entry, i) => (
                        <tr key={entry.id || i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{new Date(entry.created_at).toLocaleString()}</td>
                          <td style={{ padding: '8px' }}>{entry.admin_username || entry.admin_id}</td>
                          <td style={{ padding: '8px' }}>{entry.user_name || entry.user_id}</td>
                          <td style={{ padding: '8px' }}>{entry.slot_display_name || `#${entry.stamp_id}`}</td>
                          <td style={{ padding: '8px' }}>
                            <span style={{
                              padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                              background: entry.action === 'grant' ? '#d4edda' : entry.action === 'revoke' ? '#f8d7da' : '#e2e3e5',
                              color: entry.action === 'grant' ? '#155724' : entry.action === 'revoke' ? '#721c24' : '#383d41',
                            }}>
                              {entry.action}
                            </span>
                          </td>
                          <td style={{ padding: '8px', color: '#666', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.reason || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {auditTotal > AUDIT_LIMIT && (
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '12px' }}>
                    <button style={btnSecondary} disabled={auditPage === 0} onClick={() => fetchAuditLog(auditPage - 1)}>Previous</button>
                    <span style={{ padding: '8px', color: '#888', fontSize: '13px' }}>
                      Page {auditPage + 1} of {Math.ceil(auditTotal / AUDIT_LIMIT)}
                    </span>
                    <button style={btnSecondary} disabled={(auditPage + 1) * AUDIT_LIMIT >= auditTotal} onClick={() => fetchAuditLog(auditPage + 1)}>Next</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ========== MODALS ========== */}

        {/* Create Slot Modal */}
        {showCreate && (
          <div style={modalOverlay} onClick={() => setShowCreate(false)}>
            <div style={modalBox} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 16px 0' }}>Create New Slot</h3>
              <form onSubmit={handleCreate}>
                {renderSlotForm(createForm, setCreateForm)}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button type="button" style={btnSecondary} onClick={() => setShowCreate(false)}>Cancel</button>
                  <button type="submit" style={btnPrimary}>Create Slot</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Slot Modal */}
        {editSlot && (
          <div style={modalOverlay} onClick={() => setEditSlot(null)}>
            <div style={modalBox} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 16px 0' }}>Edit Slot #{editSlot.stamp_id}</h3>
              <form onSubmit={handleUpdate}>
                {renderSlotForm(editForm, setEditForm)}

                {/* Wipe existing unlocks checkbox — only when attraction is changing */}
                {attractionChangedInEdit && editSlot.unlock_count > 0 && (
                  <div style={{ marginBottom: '16px', padding: '12px', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px' }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={editWipeExisting}
                        onChange={e => setEditWipeExisting(e.target.checked)}
                        style={{ marginTop: '2px' }}
                      />
                      <span>
                        <strong>Wipe existing unlocks during reassignment</strong>
                        <br />
                        <span style={{ color: '#888', fontSize: '12px' }}>
                          By default, users who already unlocked this slot keep their stamp. Check this only to reset all {editSlot.unlock_count} existing unlock(s).
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button type="button" style={btnSecondary} onClick={() => setEditSlot(null)}>Cancel</button>
                  <button type="submit" style={btnPrimary}>Save Changes</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Grant/Revoke Modal (from slot row) */}
        {grantModal && (
          <div style={modalOverlay} onClick={() => setGrantModal(null)}>
            <div style={modalBox} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 16px 0' }}>
                {grantModal.action === 'grant' ? 'Grant' : 'Revoke'} Stamp #{grantModal.stamp_id}
              </h3>
              <form onSubmit={handleGrantRevoke}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={labelStyle}>User ID</label>
                  <input style={inputStyle} type="number" required value={grantForm.user_id} onChange={e => setGrantForm(p => ({ ...p, user_id: e.target.value }))} placeholder="Enter user ID" />
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Reason (required, 5-500 chars)</label>
                  <textarea style={{ ...inputStyle, minHeight: '60px' }} required minLength={5} maxLength={500} value={grantForm.reason} onChange={e => setGrantForm(p => ({ ...p, reason: e.target.value }))}
                    placeholder={grantModal.action === 'grant' ? 'e.g. Manual grant for event winner' : 'e.g. Fraudulent scan detected'} />
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button type="button" style={btnSecondary} onClick={() => setGrantModal(null)}>Cancel</button>
                  <button type="submit" style={grantModal.action === 'grant' ? btnPrimary : btnDanger}>
                    {grantModal.action === 'grant' ? 'Grant Stamp' : 'Revoke Stamp'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Manual Grant/Revoke Modal (from user lookup) */}
        {manualGrantModal && (
          <div style={modalOverlay} onClick={() => setManualGrantModal(null)}>
            <div style={modalBox} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 16px 0' }}>
                {manualGrantModal.action === 'grant' ? 'Grant' : 'Revoke'} Stamp #{manualGrantModal.stamp_id} for User #{manualGrantModal.user_id}
              </h3>
              <form onSubmit={handleManualGrantRevoke}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Reason (required, 5-500 chars)</label>
                  <textarea style={{ ...inputStyle, minHeight: '60px' }} required minLength={5} maxLength={500} value={manualGrantReason} onChange={e => setManualGrantReason(e.target.value)}
                    placeholder={manualGrantModal.action === 'grant' ? 'e.g. Manual grant for event winner' : 'e.g. Fraudulent scan detected'} />
                </div>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button type="button" style={btnSecondary} onClick={() => setManualGrantModal(null)}>Cancel</button>
                  <button type="submit" style={manualGrantModal.action === 'grant' ? btnPrimary : btnDanger}>
                    {manualGrantModal.action === 'grant' ? 'Grant Stamp' : 'Revoke Stamp'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Hard Delete Confirmation Modal */}
        {deleteTarget && (
          <div style={modalOverlay} onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}>
            <div style={modalBox} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 16px 0', color: '#c33' }}>
                Permanently Delete Slot #{deleteTarget.stamp_id}?
              </h3>
              <p style={{ marginBottom: '8px', color: '#666', lineHeight: '1.5' }}>
                This will permanently delete the slot <strong>{deleteTarget.display_name || `#${deleteTarget.stamp_id}`}</strong> AND all <strong>{deleteTarget.unlock_count || 0}</strong> unlock record(s). This cannot be undone.
              </p>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Type DELETE to confirm</label>
                <input
                  style={inputStyle}
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button style={btnSecondary} onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}>Cancel</button>
                <button
                  style={{ ...btnDanger, opacity: deleteConfirmText === 'DELETE' ? 1 : 0.4 }}
                  disabled={deleteConfirmText !== 'DELETE'}
                  onClick={handleHardDelete}
                >
                  Yes, Delete Permanently
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StampbookConfiguration;
